/**
 * Client-side section access helpers.
 * Must stay in sync with server/models/agentPermissions.model.js deriveNavSections.
 */

export const PHONE_SCOPED_SECTIONS = new Set([
  "templates",
  "campaigns",
  "flows",
  "contacts_management",
  "downloads",
  "phone_numbers",
  "livechats.own",
  "livechats.all",
]);

function isPhoneGrant(phone_id) {
  return phone_id !== null && phone_id !== undefined && String(phone_id).trim() !== "";
}

/**
 * Whether the agent has a phone-level grant for a single section.
 */
export function hasPhoneSectionGrant(permissions, section) {
  if (!Array.isArray(permissions)) return false;
  return permissions.some(
    (p) => p.section === section && isPhoneGrant(p.phone_id)
  );
}

/**
 * Check menu / route access for one section or an OR-list of sections.
 * @param {string|string[]} sectionOrSections
 * @param {string[]} sections - flat navigable sections from server
 * @param {Array<{section, phone_id}>} permissions - agent permission rows
 */
export function hasSectionAccess(sectionOrSections, sections, permissions = []) {
  const list =
    typeof sectionOrSections === "string"
      ? [sectionOrSections]
      : sectionOrSections || [];

  return list.some((sec) => {
    if (!sec) return true;
    if (PHONE_SCOPED_SECTIONS.has(sec)) {
      // Super admins get phone-scoped sections via flat `sections` (getAllSections).
      // Agents need an explicit phone grant in `permissions` and/or derived `sections`.
      return (
        (Array.isArray(sections) && sections.includes(sec)) ||
        hasPhoneSectionGrant(permissions, sec)
      );
    }
    return Array.isArray(sections) && sections.includes(sec);
  });
}

/**
 * Derive navigable sections on the client (mirrors server deriveNavSections).
 */
export function deriveNavSections(permRows) {
  const out = new Set();
  for (const r of permRows || []) {
    const { section, phone_id } = r;
    if (PHONE_SCOPED_SECTIONS.has(section)) {
      if (isPhoneGrant(phone_id)) out.add(section);
    } else if (phone_id === null || phone_id === undefined) {
      out.add(section);
    }
  }
  return [...out];
}
